import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Undo2, Check, Copy } from "lucide-react";
import { buildCompare, STATUS_CLS } from "../lib/aiCompare";
import { parseNumber } from "../lib/clipboardGrid";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("lt-LT") : "");
const copy = async (v, t) => { try { await navigator.clipboard.writeText(String(v ?? "")); toast.success(t("ah_copied")); } catch { toast.error(t("ai_copy_failed")); } };

function Cell({ f, line, onRevert, onKeep, t }) {
    const changed = f.status === "changed" || f.status === "big" || f.status === "kept";
    return (
        <td data-testid={`cmp-${f.key}-${line.idx}`} data-status={f.status} className={`px-1.5 py-1 align-top border-r border-[#182232] ${STATUS_CLS[f.status]}`}>
            <div className="text-[10px] text-slate-400">AI: <span className="font-mono">{String(f.ai ?? "") || "—"}</span></div>
            <div className="font-mono text-sm">{line.row ? (String(f.cur ?? "") || "—") : "—"}</div>
            {f.edit && <div className="text-[10px] text-slate-400 leading-tight">{f.edit.by} · {fmtDate(f.edit.at)}</div>}
            {line.row && changed && (
                <div className="flex gap-1 mt-0.5">
                    <button data-testid={`cmp-revert-${f.key}-${line.idx}`} title={t("ah_revert")} onClick={() => onRevert(line, f)} className="p-0.5 hover:text-white"><Undo2 className="w-3 h-3" /></button>
                    {f.status !== "kept" && <button data-testid={`cmp-keep-${f.key}-${line.idx}`} title={t("ah_keep")} onClick={() => onKeep(line, f)} className="p-0.5 hover:text-white"><Check className="w-3 h-3" /></button>}
                    <button data-testid={`cmp-copy-${f.key}-${line.idx}`} title={t("ah_copy")} onClick={() => copy(f.cur, t)} className="p-0.5 hover:text-white"><Copy className="w-3 h-3" /></button>
                </div>
            )}
        </td>
    );
}

// AI analysis vs current table: green same, yellow changed, red deleted / >10 %, blue manual rows.
export function AiCompareTable({ analysis, rows, setRows, isEmptyRow, onRestore, t }) {
    const { lines, manual } = useMemo(() => buildCompare(analysis, rows, isEmptyRow), [analysis, rows, isEmptyRow]);
    const fields = lines[0]?.fields || [];
    const setRow = (id, fn) => setRows((p) => p.map((r) => (r.id === id ? fn(r) : r)));
    const onRevert = (line, f) => setRow(line.row.id, (r) => {
        const edits = { ...(r.edits || {}) }; delete edits[f.target];
        return { ...r, [f.target]: f.num ? parseNumber(f.ai) : String(f.ai ?? ""), edits };
    });
    const onKeep = (line, f) => setRow(line.row.id, (r) => ({ ...r, edits: { ...(r.edits || {}), [f.target]: { ...(r.edits?.[f.target] || {}), kept: true } } }));
    const counts = lines.reduce((c, l) => {
        if (l.state === "deleted") { c.big = (c.big || 0) + 1; return c; }
        l.fields.forEach((f) => { c[f.status] = (c[f.status] || 0) + 1; });
        return c;
    }, {});
    return (
        <div className="space-y-2">
            <div className="flex items-center gap-3 flex-wrap text-xs font-mono">
                <span data-testid="cmp-count-same" className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-200">{t("ah_same")}: {(counts.same || 0) + (counts.kept || 0)}</span>
                <span data-testid="cmp-count-changed" className="px-2 py-0.5 rounded bg-yellow-400/20 text-yellow-100">{t("ah_changed")}: {counts.changed || 0}</span>
                <span data-testid="cmp-count-big" className="px-2 py-0.5 rounded bg-rose-500/20 text-rose-200">{t("ah_big")}: {counts.big || 0}</span>
                <span data-testid="cmp-count-manual" className="px-2 py-0.5 rounded bg-blue-500/15 text-blue-200">{t("ah_manual")}: {manual.length}</span>
                <button data-testid="cmp-restore-all" onClick={onRestore} className="ml-auto px-2.5 py-1 rounded border border-rose-500/50 text-rose-200 hover:bg-rose-500/10">{t("ah_restore_all")}</button>
            </div>
            <div className="overflow-auto max-h-[56vh] border border-[#1e293b] rounded-md">
                <table className="text-sm border-collapse">
                    <thead className="sticky top-0 bg-[#0b0f17] z-10">
                        <tr>{[t("ah_state"), ...fields.map((f) => t(`ai_f_${f.key}`))].map((h) => <th key={h} className="px-1.5 py-1.5 text-left text-[11px] font-mono uppercase text-slate-400 border-r border-[#1e293b] whitespace-nowrap">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                        {lines.map((l) => (
                            <tr key={l.idx} data-testid={`cmp-line-${l.idx}`} className="border-t border-[#182232]">
                                <td className={`px-1.5 text-[11px] font-mono whitespace-nowrap border-r border-[#182232] ${l.state === "deleted" ? "text-rose-300" : l.state === "not_imported" ? "text-slate-500" : "text-slate-300"}`}>{t(`ah_st_${l.state}`)}</td>
                                {l.fields.map((f) => <Cell key={f.key} f={f} line={l} onRevert={onRevert} onKeep={onKeep} t={t} />)}
                            </tr>
                        ))}
                        {manual.map((r) => (
                            <tr key={r.id} data-testid={`cmp-manual-${r.id}`} className="border-t border-[#182232]">
                                <td className="px-1.5 text-[11px] font-mono text-blue-300 border-r border-[#182232]">{t("ah_st_manual")}</td>
                                {fields.map((f) => <td key={f.key} className={`px-1.5 py-1 font-mono border-r border-[#182232] ${STATUS_CLS.manual}`}>{String(r[f.target] ?? "") || "—"}</td>)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
